import { useQuery } from '@apollo/client';
import { useRouter } from '@application/utils';
import { useDebounce } from '@application/utils/hooks/useDebounce';
import { graphql } from '@gql/gql';
import type {
  ProfessionalTypeEnum,
  ServiceSearchGetServicesQuery,
  ServiceSearchGetServicesQueryVariables,
  ServiceWhereInput,
} from '@gql/graphql';
import { QueryMode } from '@gql/graphql';
import { useCallback, useMemo } from 'react';

export const SERVICE_SEARCH_GET_SERVICES = graphql(`
  query ServiceSearchGetServices(
    $servicesWhere: ServiceWhereInput
    $serviceTranslationsWhere: ServiceTranslationWhereInput
    $take: Int
  ) {
    findManyService(where: $servicesWhere, take: $take) {
      id
      proType
      translations(where: $serviceTranslationsWhere) {
        id
        url
        language
        title
      }
    }
  }
`);

interface UseGetServicesParams {
  searchString?: string;
  serviceId?: number;
  proType?: ProfessionalTypeEnum;
}

export const useGetServices = (params: UseGetServicesParams) => {
  const { serviceId, proType } = params;

  const { language } = useRouter();
  const searchString = useDebounce(params.searchString, 250);

  const variables = useMemo((): ServiceSearchGetServicesQueryVariables => {
    const where: ServiceWhereInput = {};
    if (proType) {
      where.proType = { equals: proType };
    }
    if (searchString != null) {
      where.translations = {
        some: {
          AND: [
            { language: { equals: language } },
            {
              OR: [
                { title: { contains: searchString, mode: QueryMode.insensitive } },
                { url: { contains: searchString, mode: QueryMode.insensitive } },
              ],
            },
          ],
        },
      };
    } else if (serviceId) {
      where.id = { equals: serviceId };
    }
    return {
      take: 4,
      servicesWhere: where,
      serviceTranslationsWhere: {
        language: { equals: language },
      },
    } as ServiceSearchGetServicesQueryVariables;
  }, [language, proType, searchString, serviceId]);

  const { data, error, loading, fetchMore, refetch } = useQuery<
    ServiceSearchGetServicesQuery,
    ServiceSearchGetServicesQueryVariables
  >(SERVICE_SEARCH_GET_SERVICES, {
    variables,
    skip: !searchString && !serviceId,
  });

  const servicesGetUrl = useCallback(
    (service: ServiceSearchGetServicesQuery['findManyService']) => {
      let translations = (service?.translations ?? []).find(
        (x) => x.language === language && x.url,
      );
      if (!translations) {
        translations = (service?.translations ?? []).find((x) => x.url);
      }
      return translations?.url ?? undefined;
    },
    [language],
  );

  return {
    services: data?.findManyService ?? [],
    servicesError: error,
    servicesLoading: loading,
    servicesVariables: variables,
    servicesFetchMore: fetchMore,
    servicesRefetch: refetch,
    servicesGetUrl,
  };
};
